import { useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useQuery } from '@apollo/client';
import { AnimatePresence, motion } from 'motion/react';

import CustomImage from '../custom-image/CustomImage';
import Button from '../button/Button';
import IconUI from '../icon/IconUI';
import { useVideoLightbox } from '../../context/VideoLightboxContext';
import { useIsDesktop } from '../../context/ThemeContextManagement';
import { deepDiveQueries } from './StaffDeepDive.query.wp';
import { transformStaffDeepDive } from './StaffDeepDive.transform.wp';
import RichText from '../rich-text/RichText';

function StaffDeepDiveMedia({ card, deepDive, loading }) {
  const { open } = useVideoLightbox();

  if (deepDive?.video) {
    const firstName = (card.heading || '').split(' ')[0];
    const poster = deepDive.videoThumbnail || card.image;

    return (
      <div className="staffDeepDive-media">
        <CustomImage
          image={poster}
          fill={true}
          sizes="(max-width: 1024px) 100vw, 45vw"
        />
        <div className="staffDeepDive-mediaOverlay">
          <Button
            buttonLabel={`Meet ${firstName}`}
            icon="play"
            onClick={() => open(deepDive.video, poster?.sourceUrl)}
          />
        </div>
      </div>
    );
  }

  // Staff photo — also the instant-render state while the query is in flight.
  return (
    <div className="staffDeepDive-media" data-loading={loading || undefined}>
      <CustomImage
        image={card.image}
        fill={true}
        sizes="(max-width: 1024px) 100vw, 45vw"
      />
    </div>
  );
}

function StaffDeepDivePanel({ card, postType, onClose }) {
  const isDesktop = useIsDesktop();
  const closeButtonRef = useRef(null);
  const previousFocusRef = useRef(null);

  const query = deepDiveQueries[postType] || deepDiveQueries.staff;
  const { data, loading, error } = useQuery(query, {
    variables: { id: card.databaseId },
    skip: !card.databaseId,
    // The client defaults to no-cache; deep-dive content is static enough to
    // cache so reopening a card never refetches.
    fetchPolicy: 'cache-first',
  });

  const deepDive = useMemo(() => transformStaffDeepDive(data), [data]);

  if (error) {
    console.warn('StaffDeepDive query failed:', error.message);
  }

  // Lock page scroll while open (see CardGridFilters / VideoLightbox).
  useEffect(() => {
    window.lenis?.stop();
    // document.body.style.overflow = 'hidden';
    return () => {
      window.lenis?.start();
      // document.body.style.overflow = '';
    };
  }, []);

  // Escape closes the panel.
  useEffect(() => {
    const onKeyDown = (event) => {
      if (event.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [onClose]);

  // Move focus into the dialog, restore it to the card on close.
  useEffect(() => {
    previousFocusRef.current = document.activeElement;
    closeButtonRef.current?.focus();
    return () => previousFocusRef.current?.focus?.();
  }, []);

  const slideAxis = isDesktop ? 'x' : 'y';

  return (
    <motion.div
      className="staffDeepDive"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      transition={{ duration: 0.25, ease: 'easeOut' }}
    >
      <div className="staffDeepDive-scrim" onClick={onClose} />

      <motion.div
        className="staffDeepDive-panel"
        role="dialog"
        aria-modal="true"
        aria-labelledby={`staff-deep-dive-${card.databaseId}`}
        initial={{ [slideAxis]: '100%' }}
        animate={{ [slideAxis]: '0%' }}
        exit={{ [slideAxis]: '100%' }}
        transition={{ duration: 0.35, ease: 'easeInOut' }}
      >
        <button
          ref={closeButtonRef}
          type="button"
          className="staffDeepDive-close"
          aria-label="Close"
          onClick={onClose}
        >
          <IconUI icon={'close'} theme={'light'} padding />
        </button>

        <div className="staffDeepDive-inner" data-lenis-prevent>
          <div className="staffDeepDive-header">
            <h2
              id={`staff-deep-dive-${card.databaseId}`}
              className="staffDeepDive-heading step-5"
            >
              {card.heading}
            </h2>
            {card.subheading && (
              <p className="text--size-s">{card.subheading}</p>
            )}
          </div>

          {card.image && (
            <StaffDeepDiveMedia
              card={card}
              deepDive={deepDive}
              loading={loading}
            />
          )}

          {loading && (
            <div className="staffDeepDive-loading" aria-hidden="true">
              <span className="staffDeepDive-skeleton" />
              <span className="staffDeepDive-skeleton" />
              <span className="staffDeepDive-skeleton" />
            </div>
          )}

          {deepDive?.deepDiveHtml && (
            <RichText
              className="staffDeepDive-sectionBody"
              html={deepDive.deepDiveHtml}
            />
          )}
        </div>
      </motion.div>
    </motion.div>
  );
}

export default function StaffDeepDive({ card, postType = 'staff', onClose }) {
  const [mounted, setMounted] = useState(false);
  useEffect(() => setMounted(true), []);

  if (!mounted) return null;

  return createPortal(
    <AnimatePresence>
      {card && (
        <StaffDeepDivePanel
          key={card.databaseId}
          card={card}
          postType={postType}
          onClose={onClose}
        />
      )}
    </AnimatePresence>,
    document.body
  );
}
